import {
  Box,
  LuxAccordion,
  LuxIconButton,
  LuxModal,
  LuxModalHeader,
  LuxRadioButton,
  LuxSelectablePill,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import { ErrorMessage, Loader } from '@gemini/lux-ds-pms';
import useGetAppointments from '@hooks/useGetAppointments';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AppointmentOption,
  AppointmentsContainer,
  AppointmentsWrapper,
  AppointmentTypeWrapper,
  AppTypeInfoModalContentBox,
  AppointmentGridItem,
} from './AppointmentTypePage.styles';
import { useOnlineBookingStore } from '../../store/onlineBookingStore';
import { AppointmentTypeInfo, usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { useTranslation } from 'react-i18next';
import { PatientPill } from '@components/BookingDetails/BookingDetails.styles';
import { AppTypeDTO } from '@utils/interfaces';
import { MultiplePatientsNavigator } from '@components/MultiplePatientsNavigator/MultiplePatientsNavigator';
import { handleOnEnterKeyPress } from '@utils/functions';
import { useEvent } from '@gemini/fe-utils';
import { useHistory } from 'react-router-dom';
import {
  IAutoContinueCheckEvent,
  IChangeEvent,
  ISaveEvent,
  OBEvents,
} from '@utils/events/events';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { Helmet } from 'react-helmet';
import useGetTextColor from '@hooks/useGetTextColor';
import RouteContainer from '@components/Steps/RouteContainer';
//ICONS
import Icon_CL from '@assets/icons/icon_CL.svg';
import Icon_EEX from '@assets/icons/icon_EEX.svg';
import useAnalytics from '@hooks/useAnalytics';
import {
  MobileBreakpoint,
  TabletBreakpoint,
} from '@utils/consts/breakpoints.consts';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';
import { ModalContentWrapper } from '@components/MoreInformationModal/MoreInformationModal.styles';

interface AppTypeProps {
  setEnableButton: (val: boolean) => void;
  focusRef: React.RefObject<HTMLDivElement>;
}

const AppointmentTypePage = ({ setEnableButton, focusRef }: AppTypeProps) => {
  const { t } = useTranslation();
  const { trackPageAnalytics } = useAnalytics();
  const theme = useTheme();
  const { getTextColor } = useGetTextColor();
  const { subscribe, emit } = useEvent();
  const history = useHistory();
  const setFormStep = useOnlineBookingStore(state => state.setFormStep);
  const practiceId = useOnlineBookingStore(state => state.practiceId);
  const brandName = useOnlineBookingStore(state => state.brandName);
  const appTypeIdFromUrl = useOnlineBookingStore(
    state => state.appTypeIdFromUrl,
  );
  const { data, isLoading, error } = useGetAppointments({
    practiceIdValue: practiceId,
  });
  const patients = usePatientStore(state => state.patients);
  const updatePatient = usePatientStore(state => state.updatePatient);
  const currentPatient = useOnlineBookingStore(state => state.currentPatient);
  const isStartedNewPatient = useOnlineBookingStore(
    state => state.isStartedNewPatient,
  );
  const getCurrentPatient = usePatientStore(state => state.getCurrentPatient);

  const isMobile = useMediaQuery(`(max-width:${MobileBreakpoint}px)`);

  //ANALYTICS (RONA only) - Question 1
  useEffect(() => {
    if (brandName) {
      trackPageAnalytics({
        page: FlowsPages.APPOINTMENT_TYPE,
        patients,
      });
    }
  }, []);

  const [selectedAppointments, setSelectedAppointments] = useState<
    Array<AppointmentTypeInfo | undefined>
  >([]);

  // FURTHER QUESTIONS MANAGEMENT - BEGIN ************************************
  const clxType = useMemo(() => {
    return data?.find(type => type.id === 'clx');
  }, [data]);

  const anAppointmentHasBeenSelected = useMemo(() => {
    return !!selectedAppointments[currentPatient];
  }, [selectedAppointments, currentPatient]);

  const isMultipatientFlow = useMemo(() => {
    return (
      isStartedNewPatient || patients.filter(patient => patient).length > 1
    );
  }, [isStartedNewPatient, patients]);

  const showCLBeforeQuestionFromConfig = useOnlineBookingStore(
    state =>
      state.generalConfig?.customization?.showQuestionContactLensesBefore ??
      false,
  );
  const showEyeExamWithUsQuestionFromConfig = useOnlineBookingStore(
    state =>
      state.generalConfig?.customization?.showQuestionEyeExamWithUs ?? false,
  );

  const [answersMap, setAnswersMap] = useState<
    Record<
      number,
      { clBefore: string | undefined; eyeExamWithUs: boolean | undefined }
    >
  >({});
  const [showQuestions, setShowQuestions] = useState<{
    showCLBeforeQuestion: boolean;
    showEyeExamWithUsQuestion: boolean;
  }>({
    showCLBeforeQuestion: false,
    showEyeExamWithUsQuestion: false,
  });

  // handler for CL Before question change
  const handleCLBeforeChange = (value: string) => {
    setAnswersMap(prev => ({
      ...prev,
      [currentPatient]: {
        ...prev[currentPatient],
        clBefore: value,
      },
    }));
  };

  // handler for Eye Exam With Us question change
  const handleEyeExamWithUsChange = (value: string) => {
    setAnswersMap(prev => ({
      ...prev,
      [currentPatient]: {
        ...prev[currentPatient],
        eyeExamWithUs: value === 'yes',
      },
    }));
  };

  useEffect(() => {
    // Initialize answersMap with existing patients from store
    const initialAnswers: Record<
      number,
      { clBefore: string | undefined; eyeExamWithUs: boolean }
    > = {};
    patients.forEach((p, i) => {
      initialAnswers[i] = {
        clBefore: p?.appointmentType?.clBeforeAnswer,
        eyeExamWithUs: p?.appointmentType?.eyeExamWithUsAnswer ?? false,
      };
    });
    setAnswersMap(initialAnswers);
  }, [patients, clxType]);

  // FURTHER QUESTIONS MANAGEMENT - END **************************************

  const appFromUrl = useMemo(() => {
    if (appTypeIdFromUrl && data) {
      const name = data?.find(d => d.id === appTypeIdFromUrl)?.title;
      if (name)
        return {
          appointmentType: appTypeIdFromUrl,
          name: name,
        };
      else return undefined;
    }
    return undefined;
  }, [data, appTypeIdFromUrl]);

  const [AppTypeInfoModal, setAppTypeInfoModal] = useState<{
    title: string;
    content: { title: string; content: string }[];
  } | null>(null);

  const selectedRef =
    useRef<Array<AppointmentTypeInfo | undefined>>(selectedAppointments);

  // update showQuestions state when currentPatient change
  useEffect(() => {
    const selected = selectedRef.current[currentPatient];
    const appointmentType = selected?.appointmentType;

    setShowQuestions({
      showCLBeforeQuestion: !!(
        appointmentType?.includes('cl') && showCLBeforeQuestionFromConfig
      ),
      showEyeExamWithUsQuestion: showEyeExamWithUsQuestionFromConfig,
    });
  }, [
    currentPatient,
    showCLBeforeQuestionFromConfig,
    showEyeExamWithUsQuestionFromConfig,
  ]);

  const otherPatientChoice = useCallback(
    (appointmentType: string) => {
      const isCLType = appointmentType.includes('cl');
      const patientsWithSameChoice = patients.filter(
        (p, i) =>
          selectedAppointments[i]?.appointmentType &&
          (isCLType
            ? selectedAppointments[i].appointmentType?.includes('cl')
            : selectedAppointments[i].appointmentType === appointmentType) &&
          currentPatient !== i,
      );

      if (patientsWithSameChoice.length === 0) {
        return false;
      } else {
        let name;
        if (patientsWithSameChoice.length === 1) {
          name =
            patientsWithSameChoice[0]?.patientDetails &&
            patientsWithSameChoice[0].patientDetails?.firstName
              ? patientsWithSameChoice[0].patientDetails.firstName +
                t('appointmentTypePage.patientSelection')
              : t('appointmentTypePage.newPatientSelection');
        } else {
          name = t('appointmentTypePage.twoPatientsSelection');
        }
        return (
          <PatientPill
            customTextColor={getTextColor(
              theme.palette.LUX.obTokens.bgColorBaseDefault,
            )}
            customColor={theme.palette.LUX.obTokens.bgColorBaseDefault}
            customBorderColor={
              theme.palette.LUX.obTokens.bordersColorNeutralSecondary
            }
          >
            <LuxIconButton
              icon="UserIcon"
              variant="bare"
              size="small"
              sx={{ pointerEvents: 'none' }}
            />
            <LuxTypography variant="textXs">{name}</LuxTypography>
          </PatientPill>
        );
      }
    },
    [currentPatient, patients, selectedAppointments],
  );

  const onAppointmentClick = (type: AppTypeDTO) => {
    const newApps =
      selectedAppointments.length > 0
        ? selectedAppointments.map((a, i) =>
            i === currentPatient
              ? {
                  appointmentType: type.id,
                  name: type.title,
                  idTranslation: type.idTranslation,
                }
              : a,
          )
        : [
            {
              appointmentType: type.id,
              name: type.title,
              idTranslation: type.idTranslation,
            },
          ];

    console.log('#us', newApps);
    setAnswersMap(prev => ({
      ...prev,
      [currentPatient]: {
        clBefore: undefined,
        eyeExamWithUs: undefined,
      },
    }));
    setSelectedAppointments(newApps);

    // send this evento to inform that an appType selection has been clicked. Set shouldAutoContinue to true to trigger auto continue in Steps
    emit<IAutoContinueCheckEvent>(OBEvents.shouldAutoContinue, {
      shouldAutoContinue: !brandName && !isMultipatientFlow,
    });
  };

  useEffect(() => {
    const patient = getCurrentPatient(currentPatient);
    const oldAppointment = patient?.appointmentType?.appointmentType;
    const newAppointment =
      selectedAppointments[currentPatient]?.appointmentType;
    const patientSchedule = patient?.scheduleExam;

    if (
      oldAppointment &&
      newAppointment &&
      !(oldAppointment === newAppointment) &&
      patientSchedule
    ) {
      console.log('## Appointment Change - Event EMIT ##');
      emit<IChangeEvent>(OBEvents.apptChange, {
        type: 'appointment',
      });
    }
    setShowQuestions(prev => {
      return {
        ...prev,
        showCLBeforeQuestion: !!(
          newAppointment?.includes('cl') && showCLBeforeQuestionFromConfig
        ),
      };
    });
  }, [
    selectedAppointments,
    showCLBeforeQuestionFromConfig,
    getCurrentPatient,
    currentPatient,
  ]);

  // check enable/disable button based on selected appointments and answers
  useEffect(() => {
    selectedRef.current = selectedAppointments;

    if (
      showQuestions.showCLBeforeQuestion &&
      !answersMap[currentPatient].clBefore
    ) {
      setEnableButton(false);
      return;
    }

    if (selectedAppointments.findIndex(p => p) !== -1) {
      const allSelected = patients.every((p, i) => {
        return (p && selectedAppointments[i]) || !p;
      });
      setEnableButton(allSelected);
    } else {
      setEnableButton(false);
    }
  }, [selectedAppointments, patients, showQuestions, answersMap]);

  // Set initial selected appointments based on patients
  useEffect(() => {
    const updatedAppointments = patients.map(p =>
      p ? p.appointmentType : undefined,
    );
    setSelectedAppointments(updatedAppointments);
  }, [patients]);

  // Retrieve the appointment type from the URL if it exists
  useEffect(() => {
    if (
      appFromUrl &&
      data &&
      selectedAppointments.every(a => typeof a === 'undefined') &&
      patients.every(p => typeof p === 'undefined')
    ) {
      setSelectedAppointments([appFromUrl, undefined, undefined]);
      // send this event to inform that an appType selection has been clicked
      emit<IAutoContinueCheckEvent>(OBEvents.shouldAutoContinue, {
        shouldAutoContinue: true,
      });
    }
  }, [appFromUrl, data]);

  useEffect(() => {
    const unsubscribe = subscribe<ISaveEvent>(
      OBEvents.apptSave,
      payload => {
        if (payload.currentStep === StepsStep.APPOINTMENT_TYPE) {
          selectedRef.current.forEach((selected, index) => {
            if (selected) {
              const currentPatient = patients[index];
              const isModified =
                currentPatient &&
                currentPatient.scheduleExam &&
                currentPatient.appointmentType.appointmentType !==
                  selected.appointmentType;
              updatePatient(index, FormIds.AppointmentType, {
                appointmentType: {
                  appointmentType:
                    answersMap[index].clBefore || selected.appointmentType,
                  idTranslation: selected.idTranslation,
                  name: selected.name,
                  isModified: isModified,
                  clBeforeAnswer: answersMap[index].clBefore,
                  eyeExamWithUsAnswer: answersMap[index].eyeExamWithUs,
                },
              });
            }
          });
          history.push(`${payload.propPage.next.url}`);
          setFormStep(payload.propPage.next.step);
        }
      },
      {
        self: true,
      },
    );

    return () => unsubscribe();
  }, [selectedRef, patients, history, setFormStep, answersMap]);

  const appTypeIcon = useMemo(() => {
    const icon: Partial<Record<string, ReactNode>> = {
      CL: <Icon_CL />,
      EEX: <Icon_EEX />,
    };
    return icon;
  }, []);

  const [isBottomModal, setIsBottomModal] = useState<boolean>(
    window.innerWidth > TabletBreakpoint,
  );

  window.addEventListener('resize', () => {
    if (window.innerWidth > TabletBreakpoint) {
      setIsBottomModal(true);
    } else {
      setIsBottomModal(false);
    }
  });

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_appointment_type_title')}</title>
      </Helmet>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <ErrorMessage>{/* {error.message} */}</ErrorMessage>
      ) : (
        data && (
          <Stack direction="column" gap="1rem">
            <RouteContainer focusRef={focusRef}>
              <AppointmentsWrapper>
                <AppointmentsContainer>
                  {data.map(type => (
                    <AppointmentGridItem key={type.id}>
                      <AppointmentOption
                        minHeight={!brandName}
                        selected={
                          selectedAppointments[currentPatient]
                            ? selectedAppointments[currentPatient].name ===
                              type.title
                            : false
                        }
                        selectedColor={
                          theme.palette.LUX.obTokens?.bordersColorBrandPrimary
                        }
                        color={
                          theme.palette.LUX.obTokens
                            ?.bordersColorNeutralSecondary
                        }
                        onClick={_ => onAppointmentClick(type)}
                        onKeyDown={_ =>
                          handleOnEnterKeyPress(
                            _,
                            () => onAppointmentClick(type),
                            true,
                            true,
                          )
                        }
                      >
                        <AppointmentTypeWrapper>
                          <LuxSelectablePill
                            selectable={false}
                            tabIndex={-1}
                            size="medium"
                            text={
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{
                                  background: 'transparent!important',
                                }}
                              >
                                {appTypeIcon[type.idTranslation ?? ''] && (
                                  <span style={{ flexShrink: 0 }}>
                                    {appTypeIcon[type.idTranslation ?? '']}
                                  </span>
                                )}
                                <LuxTypography
                                  variant="textMedium"
                                  style={{ whiteSpace: 'normal' }}
                                >
                                  {brandName && type.idTranslation
                                    ? t(
                                        `appointmentTypePage.${brandName}.${type.idTranslation}.title`,
                                      )
                                    : t(type.title)}
                                </LuxTypography>
                              </Box>
                            }
                            styleWrapLuxTypography={{
                              display: 'inline-grid',
                            }}
                            customColor={
                              theme.palette.LUX.obTokens.bgColorBaseSecondary
                            }
                            customBorderColor={
                              theme.palette.LUX.obTokens
                                .bordersColorNeutralSecondary
                            }
                          />
                          {type.idTranslation && (
                            <LuxIconButton
                              icon="InformationIcon"
                              variant="bare"
                              sx={{
                                borderColor: '#222',
                                color: '#222',
                              }}
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                const modalContent = t(
                                  `appointmentTypePage.${brandName}.${type.idTranslation}.modal`,
                                  { returnObjects: true },
                                ) as Array<{
                                  title: string;
                                  content: string;
                                }>;
                                setAppTypeInfoModal({
                                  title:
                                    brandName && type.idTranslation
                                      ? t(
                                          `appointmentTypePage.${brandName}.${type.idTranslation}.title`,
                                        )
                                      : t(type.title),
                                  content: modalContent,
                                });
                              }}
                              onKeyDown={e =>
                                handleOnEnterKeyPress(
                                  e,
                                  () => {
                                    e.stopPropagation();
                                    const modalContent = t(
                                      `appointmentTypePage.${brandName}.${type.idTranslation}.modal`,
                                      { returnObjects: true },
                                    ) as Array<{
                                      title: string;
                                      content: string;
                                    }>;
                                    setAppTypeInfoModal({
                                      title: t(type.title),
                                      content: modalContent,
                                    });
                                  },
                                  true,
                                  true,
                                )
                              }
                            />
                          )}
                        </AppointmentTypeWrapper>
                        {type.subtitle && (
                          <LuxTypography
                            variant="textXs"
                            sx={{
                              textAlign: 'start',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                            }}
                          >
                            {t(type.subtitle)}
                          </LuxTypography>
                        )}
                        {otherPatientChoice(type.id)}
                      </AppointmentOption>
                    </AppointmentGridItem>
                  ))}
                </AppointmentsContainer>
              </AppointmentsWrapper>
            </RouteContainer>
            {showQuestions.showCLBeforeQuestion && clxType && (
              <RouteContainer
                focusRef={focusRef}
                customTitleKeyLabel={'appointmentTypePage.wornCLBefore'}
                showMultiplePatientsTabs={false}
              >
                <Box padding="1rem 1.5rem">
                  <LuxRadioButton
                    noHelper
                    noLabel
                    key={currentPatient + '-clBefore-radiobutton'}
                    options={
                      clxType.options
                        ? clxType.options.map(opt => ({
                            ...opt,
                            label: t(opt.label), //apply translate funct to the label from BFF
                          }))
                        : []
                    }
                    value={answersMap[currentPatient]?.clBefore || ''}
                    onChange={handleCLBeforeChange}
                    direction="horizontal"
                  />
                </Box>
              </RouteContainer>
            )}
            {showQuestions.showEyeExamWithUsQuestion &&
              anAppointmentHasBeenSelected &&
              selectedAppointments?.[currentPatient] && (
                <RouteContainer
                  focusRef={focusRef}
                  customTitleKeyLabel={'appointmentTypePage.eyeExamWithUs'}
                  showMultiplePatientsTabs={false}
                >
                  <Box padding="1rem 1.5rem">
                    <LuxRadioButton
                      noHelper
                      noLabel
                      key={currentPatient + '-eyeExam-radiobutton'}
                      options={[
                        { label: t('fieldsLabel.yes'), value: 'yes' },
                        { label: t('fieldsLabel.no'), value: 'no' },
                      ]}
                      value={
                        answersMap[currentPatient].eyeExamWithUs ? 'yes' : 'no'
                      }
                      onChange={handleEyeExamWithUsChange}
                      direction="horizontal"
                    />
                  </Box>
                </RouteContainer>
              )}
          </Stack>
        )
      )}
      <MultiplePatientsNavigator />
      <LuxModal
        type={isBottomModal ? 'right' : 'bottom'}
        open={!!AppTypeInfoModal}
        minWidthPerc={60}
        maxHeightPerc={85}
        resizable
        onClose={() => setAppTypeInfoModal(null)}
        header={
          <LuxModalHeader
            darkMode={false}
            enableCloseIcon
            onClose={() => setAppTypeInfoModal(null)}
            title={
              <LuxTypography
                variant="heading2"
                fontWeight={400}
                fontFamily={theme.palette.LUX.obTokens.textH2Family}
              >
                {AppTypeInfoModal?.title}
              </LuxTypography>
            }
          />
        }
      >
        <ModalContentWrapper>
          <Stack gap={'1.5rem'} margin={isMobile ? '0 -0.5rem 1.75rem' : '0'}>
            {AppTypeInfoModal?.content?.map((item, index) => (
              <LuxAccordion
                fullWidthContent
                defaultExpanded
                title={
                  <LuxTypography
                    variant="heading4"
                    paddingTop={'0.5rem'}
                    paddingBottom={'0.5rem'}
                  >
                    {item.title}
                  </LuxTypography>
                }
                key={index}
              >
                <AppTypeInfoModalContentBox
                  sx={{
                    fontFamily: theme.palette.LUX.obTokens.textSmFamily,
                  }}
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />
              </LuxAccordion>
            ))}
          </Stack>
        </ModalContentWrapper>
      </LuxModal>
    </>
  );
};

export default AppointmentTypePage;
